import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
/**
 * Hook to manage external {@link ToastNotification} selection.
 *
 * @param {boolean} [defaultOpen=true] - Hide/show the {@link ToastNotification}.
 * @returns {object}
 */
export const useToastOpen = (defaultOpen = true) => {
    const [open, setOpen] = useState(defaultOpen);
    return {
        props: { open: !!open, onClose: useCallback(() => setOpen(false), []) },
        open,
        setOpen,
    };
};
/**
 * Bronson ToastNotifications component.
 * Groups all related {@link ToastNotification}.
 * @constructor
 */
export function ToastNotifications({ children, ariaLabel, testId, ...otherProps }) {
    return (React.createElement("section", { className: "c-toast-notifications", "aria-label": ariaLabel, "data-testid": testId, ...otherProps },
        React.createElement("div", { id: "toast-notification-live-region", role: "status", "aria-live": "polite", "aria-atomic": "false" }, children)));
}
/**
 * Bronson ToastNotification component (nested).
 * @see https://bronson.vwfs.io/default/components/detail/bronson-toast-notification.html
 * @constructor
 */
export function ToastNotification({ children, className, closeLabel = 'Close Toast Notification', error, info, onClose, open = true, success, warning, testId, ...otherProps }) {
    const classNameList = classNames('c-toast-notification', {
        // Convenience props from Bronson variants.
        'c-toast-notification--default': !(info || success || warning || error),
        'c-toast-notification--info': info,
        'c-toast-notification--success': success,
        'c-toast-notification--warning': warning,
        'c-toast-notification--error': error,
        'is-closed': !open,
    }, className).trim();
    /**
     * Set the initial height to make CSS animations work properly.
     * @TODO: Should be refactored with the ReactTransitionGroup add-on, @see BRON-7532.
     */
    //   const toastRef = useRef(null)
    //   const [initialHeight, setInitialHeight] = useState(null)
    //   useEffect(() => {
    //     if (toastRef && toastRef.current) {
    //       const h = toastRef.current.clientHeight
    //       if (!!open && !initialHeight) {
    //         setInitialHeight(h)
    //       }
    //     }
    //   }, [open, initialHeight])
    return (
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     * @see BRON-11871
     */
    React.createElement("article", { className: classNameList, "aria-hidden": !open, 
        // @ts-ignore @TODO: Remove once React supports inerts, @see BRON-11871.
        inert: open ? null : '', "data-testid": testId, ...otherProps },
        React.createElement("div", { className: "c-toast-notification__content" },
            React.createElement("div", { className: "c-toast-notification__body" },
                React.createElement("p", null, children)),
            React.createElement("button", { className: "c-toast-notification__close", "aria-label": closeLabel, type: "button", onClick: onClose }))));
}
